import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, fontFamily, fontSizes, fontWeights, radii } from '../../theme/tokens';
import { useAuth } from '../../contexts/AuthContext';
import OtpInput from './OtpInput';

const PROFILE_COMPLETE_PATH = '/profile/complete';
const OTP_PENDING_KEY = 'login_otp_pending';

function getOtpPending() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(OTP_PENDING_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.mobileNumber && data?.otpSent ? data : null;
  } catch {
    return null;
  }
}

function setOtpPending(mobileNumber) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(
        OTP_PENDING_KEY,
        JSON.stringify({ mobileNumber, otpSent: true })
      );
    }
  } catch (_) {}
}

function clearOtpPending() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(OTP_PENDING_KEY);
    }
  } catch (_) {}
}

const STEP = { MOBILE: 'mobile', OTP: 'otp', NEW_USER: 'new_user' };

const LoginForm = ({ onLoginSuccess, onRegisterSuccess, stayOnPage = false }) => {
  const navigate = useNavigate();
  const newUserFormRef = useRef(null);
  const { loginMobile, sendOtp, registerMobile } = useAuth();

  const pending = getOtpPending();
  const [step, setStep] = useState(() =>
    pending?.otpSent ? STEP.OTP : STEP.MOBILE
  );
  const [mobileNumber, setMobileNumber] = useState(() => pending?.mobileNumber ?? '');
  const [otp, setOtp] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [school, setSchool] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpMessage, setOtpMessage] = useState('');

  useEffect(() => {
    if (step === STEP.NEW_USER && newUserFormRef.current) {
      newUserFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [step]);

  const handleSendOtp = async () => {
    setError('');
    const mobile = mobileNumber.trim();
    if (!mobile) {
      setError('Please enter your mobile number');
      return;
    }
    if (!/^[0-9]{10}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setOtp('');
    setOtpMessage('');
    try {
      const result = await sendOtp(mobile);
      if (result?.success) {
        setOtpPending(mobile);
        setStep(STEP.OTP);
        setOtpMessage('OTP sent. Enter the code below.');
      } else {
        setError(result?.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp('');
    setOtpMessage('');
    const mobile = mobileNumber.trim();
    if (!mobile) return;
    setLoading(true);
    setError('');
    try {
      const result = await sendOtp(mobile);
      if (result?.success) {
        setOtpPending(mobile);
        setOtpMessage('New OTP sent. Enter the new code below.');
      } else {
        setError(result?.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    setError('');
    const mobile = mobileNumber.trim();
    const code = otp.trim();
    if (!mobile || !code) {
      setError('Please enter mobile number and OTP');
      return;
    }
    if (!/^[0-9]{10}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!/^[0-9]{6}$/.test(code)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const result = await loginMobile(mobile, code);
      if (result?.success) {
        clearOtpPending();
        onLoginSuccess?.();
        if (!stayOnPage) navigate('/', { replace: true });
        return;
      }
      const isNewUser =
        result?.userNotFound === true ||
        (result?.message && /not found|register first/i.test(String(result.message)));
      if (isNewUser) {
        setStep(STEP.NEW_USER);
        setError('');
      } else {
        setError(result?.message || 'Login failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? '';
      const isNewUser = /not found|register first/i.test(String(msg)) || err?.response?.status === 404;
      if (isNewUser) {
        setStep(STEP.NEW_USER);
        setError('');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setError('');
    if (!fullName.trim() || !email.trim() || !school.trim()) {
      setError('Please fill in all fields');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    setLoading(true);
    try {
      const result = await registerMobile({
        mobileNumber: mobileNumber.trim(),
        otp: otp.trim(),
        firstName,
        lastName,
        schoolName: school.trim(),
        email: email.trim(),
      });
      if (result?.success) {
        clearOtpPending();
        onRegisterSuccess?.();
        onLoginSuccess?.();
        setTimeout(() => navigate(PROFILE_COMPLETE_PATH, { replace: true }), 0);
      } else {
        setError(result?.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearOtpPending();
    setStep(STEP.OTP);
    setError('');
  };

  const showMobile = step === STEP.MOBILE;
  const showOtp = step === STEP.OTP && !showMobile;
  const showNewUser = step === STEP.NEW_USER;

  const handleChangeNumber = () => {
    clearOtpPending();
    setStep(STEP.MOBILE);
    setOtp('');
    setOtpMessage('');
    setError('');
  };

  const maskedMobile =
    mobileNumber.length === 10
      ? '+91 ' + mobileNumber.slice(0, 2) + 'XXXXXX' + mobileNumber.slice(-2)
      : '+91 ' + mobileNumber;

  const copy = showNewUser
    ? ['Create your account', 'This number isn’t registered. Create an account to continue.']
    : showOtp
    ? ['Enter the OTP', 'Type the 6-digit code we sent to your mobile number.']
    : ['Login or create account', 'Enter your mobile number. We will send you a one-time password.'];

  const S = {
    root: { display: 'flex', flexDirection: 'column', gap: 20, fontFamily, color: colors.ink, minWidth: 0 },
    title: { margin: 0, fontSize: fontSizes.h2, fontWeight: fontWeights.bold, color: colors.ink },
    sub: { margin: 0, fontSize: fontSizes.body, lineHeight: 1.5, color: colors.slate },
    group: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
    label: { fontSize: fontSizes.small, fontWeight: fontWeights.semibold, color: colors.ink },
    field: {
      height: 52, padding: '0 16px', boxSizing: 'border-box', borderRadius: radii.input,
      border: '1.5px solid ' + colors.softBlue, background: colors.white, fontFamily,
      fontSize: fontSizes.body, color: colors.ink, width: '100%', minWidth: 0,
    },
    phoneWrap: {
      display: 'flex', alignItems: 'center', height: 56, borderRadius: radii.input,
      border: '1.5px solid ' + colors.softBlue, background: colors.white, padding: '0 16px',
      boxSizing: 'border-box', gap: 12,
    },
    prefix: {
      fontSize: fontSizes.body, fontWeight: fontWeights.semibold, color: colors.slate,
      paddingRight: 12, borderRight: '1px solid ' + colors.border,
    },
    phoneInput: {
      flexGrow: 1, minWidth: 0, height: 52, border: 0, outline: 'none', background: 'transparent',
      fontFamily, fontSize: fontSizes.bodyLg, color: colors.ink,
    },
    primary: (disabled) => ({
      minHeight: 56, border: 0, borderRadius: radii.button, background: colors.navy, color: colors.white,
      fontFamily, fontSize: fontSizes.bodyLg, fontWeight: fontWeights.semibold,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1, width: '100%',
    }),
    textBtn: {
      minHeight: 44, padding: 0, border: 0, background: 'transparent', color: colors.navy,
      fontFamily, fontSize: fontSizes.body - 1, fontWeight: fontWeights.semibold, cursor: 'pointer',
    },
    info: {
      padding: '12px 16px', borderRadius: radii.input, background: colors.tealTint, color: colors.teal,
      fontSize: fontSizes.body - 1, fontWeight: fontWeights.medium,
    },
    errorBox: {
      padding: '12px 16px', borderRadius: radii.input, background: colors.dangerTint, color: colors.danger,
      fontSize: fontSizes.small, fontWeight: fontWeights.medium,
    },
  };

  return (
    <div style={S.root}>
      <h2 style={S.title}>{copy[0]}</h2>
      <p style={S.sub}>{copy[1]}</p>

      {error ? (
        <div role="alert" style={S.errorBox}>
          {error}
        </div>
      ) : null}

      {showMobile && (
        <>
          <div style={S.group}>
            <label htmlFor="login-mobile" style={S.label}>Mobile number</label>
            <div style={S.phoneWrap}>
              <span style={S.prefix}>+91</span>
              <input
                id="login-mobile"
                type="tel"
                inputMode="numeric"
                style={S.phoneInput}
                placeholder="10-digit mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10}
              />
            </div>
          </div>
          <button type="button" style={S.primary(loading)} onClick={handleSendOtp} disabled={loading}>
            {loading ? 'Sending...' : 'Send OTP'}
          </button>
        </>
      )}

      {showOtp && (
        <>
          <div style={S.info}>
            {otpMessage || 'OTP sent. Enter the code below.'}
            <div style={{ fontSize: fontSizes.small, marginTop: 2 }}>Sent to {maskedMobile}</div>
          </div>
          <div style={S.group}>
            <label htmlFor="login-otp" style={S.label}>6-digit OTP</label>
            <OtpInput id="login-otp" value={otp} onChange={setOtp} disabled={loading} />
          </div>
          <button type="button" style={S.primary(loading)} onClick={handleContinue} disabled={loading}>
            {loading ? 'Checking...' : 'Continue'}
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" style={S.textBtn} onClick={handleResendOtp} disabled={loading}>
              Resend OTP
            </button>
            <button
              type="button"
              style={{ ...S.textBtn, color: colors.slate, fontWeight: fontWeights.regular, textDecoration: 'underline' }}
              onClick={handleChangeNumber}
              disabled={loading}
            >
              Change number
            </button>
          </div>
        </>
      )}

      {showNewUser && (
        <div ref={newUserFormRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={S.group}>
            <label htmlFor="login-fullname" style={S.label}>Full name</label>
            <input
              id="login-fullname"
              type="text"
              style={S.field}
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div style={S.group}>
            <label htmlFor="login-email" style={S.label}>Email</label>
            <input
              id="login-email"
              type="email"
              style={S.field}
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div style={S.group}>
            <label htmlFor="login-school" style={S.label}>School</label>
            <input
              id="login-school"
              type="text"
              style={S.field}
              placeholder="School"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
            />
          </div>
          <button type="button" style={{ ...S.primary(loading), marginTop: 4 }} onClick={handleCreateAccount} disabled={loading}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
          <button type="button" style={S.textBtn} onClick={handleBackToLogin} disabled={loading}>
            Back to login
          </button>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
